import { useCallback, useState } from 'react';
import { DeliveryItem } from '../types';

export interface DeliveryLifecycleCallbacks<P> {
    onSuccess?: (payload: P, result: string | void) => void;
    onError?: (error: unknown, payload: P) => void;
    onSettled?: (payload: P) => void;
}

export interface UseBaseDeliveryProps<T extends DeliveryItem, P>
    extends DeliveryLifecycleCallbacks<P> {
    apiAction: (items: T[], payload: P) => Promise<string | void>;
}

export const useBaseDelivery = <T extends DeliveryItem, P>({
    apiAction,
    onSuccess,
    onError,
    onSettled,
}: UseBaseDeliveryProps<T, P>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [validItems, setValidItems] = useState<T[]>([]);
    const [invalidCount, setInvalidCount] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [generatedResult, setGeneratedResult] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const openModal = useCallback((items: T[]) => {
        const valid = items.filter((i) => i.isValid);
        setValidItems(valid);
        setInvalidCount(items.filter((i) => !i.isValid).length);
        setSelectedIds(valid.map((i) => i.id));
        setGeneratedResult(null);
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => setIsOpen(false), []);

    const toggleSelection = useCallback((id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        );
    }, []);

    const executeAction = (payload: P) => {
        const finalItems = validItems.filter((i) => selectedIds.includes(i.id));
        if (!finalItems.length) return;

        setIsProcessing(true);

        apiAction(finalItems, payload)
            .then((result) => {
                setGeneratedResult(result || 'success');
                onSuccess?.(payload, result);
            })
            .catch((err) => {
                console.error('Delivery action failed', err);
                onError?.(err, payload);
            })
            .finally(() => {
                setIsProcessing(false);
                onSettled?.(payload);
            });
    };

    return {
        openModal,
        closeModal,
        deliveryState: {
            isOpen,
            validItems,
            invalidCount,
            selectedIds,
            generatedResult,
            isProcessing,
        },
        deliveryActions: {
            toggleSelection,
            executeAction,
            setIsOpen,
        },
    };
};
